import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { usersApi } from '@/api/users'
import { getApiErrorMessage } from '@/api/client'
import type { BadgeTone } from '@/components/ui'
import { dayjs, fromServer } from '@/lib/dates'
import { notify } from '@/lib/toast'
import type { Customer, TeamMember } from '@/types/user'

export const peopleKeys = {
  team: ['people', 'team'] as const,
  customers: ['people', 'customers'] as const,
  profile: (id: number | string) => ['people', 'profile', String(id)] as const,
}

export const SUBSCRIPTIONS = ['Single', 'Weekly', 'Unlimited', 'Double', 'Custom', 'VidChops Weekly', 'VidChops Pro'] as const
export const REFERRALS = ['Google', 'Youtube', 'Linkedin', 'Google Search', 'Facebook', 'Affiliate', 'Other'] as const
export const STAFF_ROLES = [
  { value: 'qa', label: 'QA / Editor' },
  { value: 'team-lead', label: 'Team Lead' },
  { value: 'yt-helper', label: 'YT Helper' },
  { value: 'yt-helper-lead', label: 'YT Helper Lead' },
  { value: 'admin', label: 'Admin' },
] as const

export function planTone(plan: string | null | undefined): BadgeTone {
  if (!plan) return 'gray'
  if (/pro/i.test(plan)) return 'purple'
  if (/weekly/i.test(plan)) return 'blue'
  if (/unlimited|double/i.test(plan)) return 'teal'
  if (/custom/i.test(plan)) return 'amber'
  return 'gray'
}

export type Activity = 'Active' | 'Inactive for 14 Days' | 'Inactive for 30 Days' | 'Never logged in'

/** The API returns is_on_leave as the string "0"/"1", so a plain truthiness check is wrong. */
export function isOnLeave(u: { is_on_leave?: 0 | 1 | string | null }): boolean {
  return Number(u.is_on_leave ?? 0) === 1
}

export function isHexColor(value: string | null | undefined): value is string {
  return !!value && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim())
}

/** Good-enough check before the API validates for real: one `@`, a dot in the domain, no spaces. */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim())
}

export function customerActivity(c: Pick<Customer, 'last_login_time'>): Activity {
  const last = fromServer(c.last_login_time)
  if (!last) return 'Never logged in'
  const days = dayjs().diff(last, 'day')
  if (days >= 30) return 'Inactive for 30 Days'
  if (days >= 14) return 'Inactive for 14 Days'
  return 'Active'
}

/** A simple, explainable health score: subscription status plus how recently they signed in. */
export function customerHealth(c: Pick<Customer, 'last_login_time' | 'is_active_subscription'>): {
  score: number
  label: 'Healthy' | 'Watch' | 'At risk'
  tone: BadgeTone
} {
  if (!c.is_active_subscription) return { score: 20, label: 'At risk', tone: 'red' }
  const last = fromServer(c.last_login_time)
  const days = last ? dayjs().diff(last, 'day') : 60
  const score = Math.max(0, Math.min(100, 100 - days * 2))
  if (score >= 75) return { score, label: 'Healthy', tone: 'green' }
  if (score >= 45) return { score, label: 'Watch', tone: 'amber' }
  return { score, label: 'At risk', tone: 'red' }
}

/**
 * The team and customer lists are loaded once when the workspace mounts (`usePeoplePreload`) and
 * shared from the query cache; within this window a page visit renders them without a request.
 */
export const PEOPLE_STALE_MS = 5 * 60 * 1000

export function useTeam(enabled = true) {
  return useQuery({ queryKey: peopleKeys.team, queryFn: usersApi.team, enabled, staleTime: PEOPLE_STALE_MS })
}

export function useCustomers(enabled = true) {
  return useQuery({ queryKey: peopleKeys.customers, queryFn: usersApi.customers, enabled, staleTime: PEOPLE_STALE_MS })
}

export function useProfile(id: number | string | undefined) {
  return useQuery({ queryKey: peopleKeys.profile(id ?? ''), queryFn: () => usersApi.get(id!), enabled: !!id })
}

type ActiveFlag = { client_id: number; is_active_subscription: 0 | 1 }

/**
 * Activates or deactivates one account. The cached team and customer lists are patched
 * immediately (optimistic), so only that row changes and the rest of the list never flickers;
 * the server answer is reconciled in the background and the change is rolled back on error.
 */
export function useToggleActive() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { clientId: number; active: boolean }) => usersApi.toggleActive(vars.clientId, vars.active),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: ['people'] })
      const flag: 0 | 1 = vars.active ? 1 : 0
      const patch = <T extends ActiveFlag>(list: T[] | undefined) =>
        list?.map((person) => (person.client_id === vars.clientId ? { ...person, is_active_subscription: flag } : person))
      const previous = {
        team: queryClient.getQueryData<TeamMember[]>(peopleKeys.team),
        customers: queryClient.getQueryData<Customer[]>(peopleKeys.customers),
      }
      queryClient.setQueryData<TeamMember[]>(peopleKeys.team, patch)
      queryClient.setQueryData<Customer[]>(peopleKeys.customers, patch)
      return previous
    },
    onSuccess: (_d, vars) => notify.success(vars.active ? 'Account activated' : 'Account deactivated'),
    onError: (e, _vars, previous) => {
      if (previous?.team) queryClient.setQueryData(peopleKeys.team, previous.team)
      if (previous?.customers) queryClient.setQueryData(peopleKeys.customers, previous.customers)
      notify.error(getApiErrorMessage(e))
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['people'] }),
  })
}

export function useDeleteUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (clientId: number) => usersApi.remove(clientId),
    onSuccess: (data) => {
      if (data?.error) {
        notify.error(data.error)
        return
      }
      notify.success(data?.message || 'User removed')
      void queryClient.invalidateQueries({ queryKey: ['people'] })
      void queryClient.invalidateQueries({ queryKey: ['websiteUsers'] })
    },
    onError: (e) => notify.error(getApiErrorMessage(e)),
  })
}

export function generatePassword(length = 10): string {
  const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  const arr = new Uint32Array(length)
  crypto.getRandomValues(arr)
  return Array.from(arr, (n) => chars[n % chars.length]).join('')
}
